import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import type {
  ConfigureProductUomPayload,
  Product,
  UnitOfMeasure,
} from "@/services/types/products";
import { Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type PackagingUnitRow = {
  id: string;
  uomId: string;
  factor: string;
};

const NO_BASE_UOM_VALUE = "__none__";

function getCurrentDefaultUomId(
  product: Product,
  fallbackBaseUomId: string,
): string {
  if (typeof product.default_sales_uom === "number") {
    return String(product.default_sales_uom);
  }
  return fallbackBaseUomId;
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 2,
  }).format(value);
}

function getCurrentBaseUomId(product: Product, uoms: UnitOfMeasure[]): string {
  if (typeof product.base_stock_uom === "number") {
    return String(product.base_stock_uom);
  }

  const byCode = uoms.find(
    (uom) =>
      uom.code.toLowerCase() ===
      (product.base_stock_uom_code || "").toLowerCase(),
  );
  return byCode ? String(byCode.id) : "";
}

function createEmptyRow(): PackagingUnitRow {
  return {
    id: crypto.randomUUID(),
    uomId: "",
    factor: "",
  };
}

function getUomLabel(uom: UnitOfMeasure | undefined): string {
  if (!uom) {
    return "unit";
  }
  return uom.code || uom.name;
}

export function ConfigureUomDrawer({
  open,
  onOpenChange,
  product,
  uoms,
  isSubmitting,
  onSubmit,
  embedded = false,
  canEdit = true,
  submitLabel,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: Product | null;
  uoms: UnitOfMeasure[];
  isSubmitting: boolean;
  onSubmit: (payload: ConfigureProductUomPayload) => Promise<void>;
  embedded?: boolean;
  canEdit?: boolean;
  submitLabel?: string;
}) {
  const [baseUomId, setBaseUomId] = useState("");
  const [defaultUomId, setDefaultUomId] = useState("");
  const [packagingUnits, setPackagingUnits] = useState<PackagingUnitRow[]>([]);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  useEffect(() => {
    if ((!open && !embedded) || !product) {
      return;
    }

    setSubmissionError(null);
    const nextBaseUomId = getCurrentBaseUomId(product, uoms);
    setBaseUomId(nextBaseUomId);
    setDefaultUomId(getCurrentDefaultUomId(product, nextBaseUomId));
    setPackagingUnits(
      product.uom_conversions
        .filter(
          (conversion) =>
            conversion.is_active &&
            String(conversion.to_uom) === nextBaseUomId &&
            conversion.from_uom !== conversion.to_uom,
        )
        .sort(
          (left, right) => Number(right.factor ?? 0) - Number(left.factor ?? 0),
        )
        .map((conversion) => ({
          id: crypto.randomUUID(),
          uomId: String(conversion.from_uom),
          factor: String(conversion.factor ?? ""),
        })),
    );
  }, [embedded, open, product, uoms]);

  useEffect(() => {
    const packagingUomIds = new Set(
      packagingUnits.map((row) => row.uomId).filter(Boolean),
    );
    setDefaultUomId((current) => {
      if (!current || current === baseUomId) {
        return baseUomId;
      }
      return packagingUomIds.has(current) ? current : baseUomId;
    });
  }, [baseUomId, packagingUnits]);

  const baseUom = useMemo(
    () => uoms.find((uom) => String(uom.id) === baseUomId),
    [baseUomId, uoms],
  );

  const previewSegments = useMemo(() => {
    if (!baseUom) {
      return [];
    }

    const validRows = packagingUnits
      .map((row) => {
        const factor = Number(row.factor);
        const uom = uoms.find((item) => String(item.id) === row.uomId);
        if (!uom || !Number.isFinite(factor) || factor <= 0) {
          return null;
        }
        return {
          factor,
          uom,
        };
      })
      .filter(
        (row): row is { factor: number; uom: UnitOfMeasure } => row !== null,
      )
      .sort((left, right) => right.factor - left.factor);

    if (validRows.length === 0) {
      return [];
    }

    const highest = validRows[0];
    return [
      `1 ${getUomLabel(highest.uom)} =`,
      ...validRows
        .slice(1)
        .map(
          (row) =>
            `${formatNumber(highest.factor / row.factor)} ${getUomLabel(row.uom)} =`,
        ),
      `${formatNumber(highest.factor)} ${getUomLabel(baseUom)}`,
    ];
  }, [baseUom, packagingUnits, uoms]);

  if (!product) {
    return null;
  }

  const handleBaseUomChange = (value: string) => {
    const nextBaseUomId = value === NO_BASE_UOM_VALUE ? "" : value;
    if (nextBaseUomId === baseUomId) {
      return;
    }

    setSubmissionError(null);
    setBaseUomId(nextBaseUomId);
    setPackagingUnits([]);
    setDefaultUomId(nextBaseUomId);
  };

  const handleSubmit = async () => {
    setSubmissionError(null);

    if (baseUomId) {
      const hasInvalidRow = packagingUnits.some((row) => {
        const factor = Number(row.factor);
        return (
          !row.uomId ||
          row.uomId === baseUomId ||
          !Number.isFinite(factor) ||
          factor <= 0
        );
      });

      if (hasInvalidRow) {
        setSubmissionError(
          "Complete every packaging unit and enter a conversion factor greater than zero.",
        );
        return;
      }

      const selectedUomIds = packagingUnits.map((row) => row.uomId);
      if (new Set(selectedUomIds).size !== selectedUomIds.length) {
        setSubmissionError("Each packaging unit can only be configured once.");
        return;
      }
    }

    const payload: ConfigureProductUomPayload = {
      base_stock_uom: baseUomId ? Number(baseUomId) : null,
      default_uom: baseUomId ? Number(defaultUomId || baseUomId) : null,
      packaging_units: baseUomId
        ? packagingUnits.map((row) => ({
            uom_id: Number(row.uomId),
            factor: Number(row.factor),
          }))
        : [],
    };

    await onSubmit(payload);
    onOpenChange(false);
  };

  const editor = (
    <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden">
      <div
        className={cn(
          "min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-6 pb-6",
          embedded ? "space-y-5 py-5 sm:px-8" : "space-y-8",
        )}
      >
        {embedded ? (
          <div className="space-y-1">
            <h3 className="font-medium text-lg">Edit Unit of Measure</h3>
            <p className="max-w-lg text-muted-foreground text-sm leading-snug">
              Update the base unit and the packaging conversions used across
              orders, inventory and forecasts.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              <div className="font-semibold text-2xl">
                {product.name || "Unnamed Product"}
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge className="rounded-full border-0 bg-sky-50 px-3 py-1 text-sky-700 hover:bg-sky-50">
                  {product.storage_type.charAt(0).toUpperCase() +
                    product.storage_type.slice(1)}
                </Badge>
                <Badge className="rounded-full border-0 bg-lime-200 px-3 py-1 text-lime-900 hover:bg-lime-200">
                  {product.product_brands.length}{" "}
                  {product.product_brands.length === 1 ? "brand" : "brands"}
                </Badge>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div
              className={cn("font-medium", embedded ? "text-sm" : "text-xl")}
            >
              Base unit
            </div>
            <Badge className="rounded bg-muted px-2 py-0.5 font-medium text-[11px] text-foreground hover:bg-muted">
              Base
            </Badge>
          </div>
          <Select
            value={baseUomId || NO_BASE_UOM_VALUE}
            onValueChange={handleBaseUomChange}
            disabled={!canEdit}
          >
            <SelectTrigger className="h-12 w-full rounded-lg border-border/80 bg-background text-[15px]">
              <span
                className={
                  baseUom ? "truncate" : "truncate text-muted-foreground"
                }
              >
                {baseUom ? `${baseUom.name} (${baseUom.code})` : "Select UOM"}
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                value={NO_BASE_UOM_VALUE}
                className="text-muted-foreground"
              >
                Select UOM
              </SelectItem>
              {uoms.map((uom) => (
                <SelectItem key={uom.id} value={String(uom.id)}>
                  {uom.name} ({uom.code})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="text-[13px] text-muted-foreground">
            Stock, inventory and forecasts for this SKU are tracked in this
            unit.
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h3
              className={cn("font-medium", embedded ? "text-sm" : "text-2xl")}
            >
              Order &amp; packaging units
            </h3>
            <Badge className="w-fit rounded bg-muted px-2 py-0.5 font-medium text-[11px] text-muted-foreground hover:bg-muted">
              {packagingUnits.length}{" "}
              {packagingUnits.length === 1 ? "unit" : "units"}
            </Badge>
          </div>

          <div className="space-y-3">
            {packagingUnits.length === 0 ? (
              <div className="rounded-2xl border border-border border-dashed bg-muted/10 px-4 py-6 text-center text-muted-foreground text-sm">
                No packaging units added yet.
              </div>
            ) : null}

            {packagingUnits.map((row) => {
              const selectedUom = uoms.find(
                (uom) => String(uom.id) === row.uomId,
              );

              return (
                <div
                  key={row.id}
                  data-testid={`packaging-unit-row-${row.id}`}
                  className="rounded-xl bg-muted p-3 sm:p-4"
                >
                  <div className="space-y-3 sm:hidden">
                    <div className="flex items-center gap-2">
                      <div className="min-w-0 flex-1">
                        <Select
                          value={row.uomId}
                          disabled={!canEdit}
                          onValueChange={(value) =>
                            setPackagingUnits((current) =>
                              current.map((item) =>
                                item.id === row.id
                                  ? { ...item, uomId: value }
                                  : item,
                              ),
                            )
                          }
                        >
                          <SelectTrigger className="h-10 w-full rounded-lg bg-background">
                            <SelectValue placeholder="Select UOM" />
                          </SelectTrigger>
                          <SelectContent>
                            {uoms
                              .filter((uom) => String(uom.id) !== baseUomId)
                              .map((uom) => (
                                <SelectItem key={uom.id} value={String(uom.id)}>
                                  {uom.name} ({uom.code})
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 shrink-0 rounded-full text-rose-400 hover:bg-rose-50 hover:text-rose-600"
                        disabled={!canEdit}
                        onClick={() =>
                          setPackagingUnits((current) =>
                            current.filter((item) => item.id !== row.id),
                          )
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                        <span className="sr-only">Delete unit</span>
                      </Button>
                    </div>

                    <div className="flex items-center gap-2 text-[13px]">
                      <div className="text-foreground">
                        1 {getUomLabel(selectedUom).toLowerCase()} =
                      </div>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={row.factor}
                        placeholder="0"
                        className="h-10 w-20 rounded-lg text-center"
                        disabled={!canEdit}
                        onChange={(event) =>
                          setPackagingUnits((current) =>
                            current.map((item) =>
                              item.id === row.id
                                ? {
                                    ...item,
                                    factor: event.target.value,
                                  }
                                : item,
                            ),
                          )
                        }
                      />
                      <Badge
                        variant="outline"
                        className="rounded-md bg-background px-2 py-1 font-normal text-xs"
                      >
                        {getUomLabel(baseUom)}
                      </Badge>
                      <Button
                        type="button"
                        variant="ghost"
                        className={
                          defaultUomId === row.uomId
                            ? "ml-auto h-auto rounded-full bg-lime-200 px-2 py-1 text-[11px] text-lime-950 hover:bg-lime-200"
                            : "ml-auto h-auto rounded-full px-0 py-1 text-foreground text-xs hover:bg-transparent hover:text-foreground"
                        }
                        disabled={!canEdit || !row.uomId}
                        onClick={() => setDefaultUomId(row.uomId)}
                      >
                        {defaultUomId === row.uomId ? "Default" : "Set default"}
                      </Button>
                    </div>
                  </div>

                  <div className="hidden min-w-0 sm:grid sm:grid-cols-[118px_auto_64px_auto_minmax(76px,1fr)_28px] sm:items-center sm:gap-2">
                    <div className="space-y-2">
                      <Select
                        value={row.uomId}
                        disabled={!canEdit}
                        onValueChange={(value) =>
                          setPackagingUnits((current) =>
                            current.map((item) =>
                              item.id === row.id
                                ? {
                                    ...item,
                                    uomId: value,
                                  }
                                : item,
                            ),
                          )
                        }
                      >
                        <SelectTrigger className="h-10 w-full rounded-lg bg-background">
                          <SelectValue placeholder="Select UOM" />
                        </SelectTrigger>
                        <SelectContent>
                          {uoms
                            .filter((uom) => String(uom.id) !== baseUomId)
                            .map((uom) => (
                              <SelectItem key={uom.id} value={String(uom.id)}>
                                {uom.name} ({uom.code})
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="whitespace-nowrap text-[13px]">
                      1 {getUomLabel(selectedUom).toLowerCase()} =
                    </div>

                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={row.factor}
                      placeholder="0"
                      className="h-10 rounded-lg text-center"
                      disabled={!canEdit}
                      onChange={(event) =>
                        setPackagingUnits((current) =>
                          current.map((item) =>
                            item.id === row.id
                              ? {
                                  ...item,
                                  factor: event.target.value,
                                }
                              : item,
                          ),
                        )
                      }
                    />

                    <Badge
                      variant="outline"
                      className="w-fit rounded-md bg-background px-2 py-1 font-normal text-xs"
                    >
                      {getUomLabel(baseUom)}
                    </Badge>

                    <Button
                      type="button"
                      variant="ghost"
                      className={
                        defaultUomId === row.uomId
                          ? "h-auto justify-self-end rounded-full bg-lime-200 px-2 py-1 text-[11px] text-lime-950 hover:bg-lime-200"
                          : "h-auto justify-self-end rounded-full px-0 py-1 text-foreground text-xs hover:bg-transparent hover:text-foreground"
                      }
                      disabled={!canEdit || !row.uomId}
                      onClick={() => setDefaultUomId(row.uomId)}
                    >
                      {defaultUomId === row.uomId ? "Default" : "Set default"}
                    </Button>

                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 rounded-full text-rose-400 hover:bg-rose-50 hover:text-rose-600"
                      disabled={!canEdit}
                      onClick={() =>
                        setPackagingUnits((current) =>
                          current.filter((item) => item.id !== row.id),
                        )
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="sr-only">Delete unit</span>
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          <Button
            type="button"
            variant="outline"
            className="h-11 w-full rounded-lg border-border/80 border-dashed bg-background text-sm hover:bg-muted/20"
            disabled={!canEdit || !baseUomId}
            onClick={() =>
              setPackagingUnits((current) => [...current, createEmptyRow()])
            }
          >
            <Plus className="mr-2 h-4 w-4" />
            Add unit
          </Button>
        </div>

        <div className="space-y-3">
          <div className="rounded-xl bg-muted p-4">
            <div className="mb-1 font-medium text-muted-foreground text-xs">
              <span className="mr-2">⇄</span>
              Conversion preview
            </div>
            {previewSegments.length > 0 ? (
              <div className="break-words text-sm">
                {previewSegments.join(" ")}
              </div>
            ) : (
              <div className="text-muted-foreground text-sm">
                Add packaging units with valid conversion factors to see the
                preview.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="border-t bg-background px-5 py-4 sm:px-8 sm:py-5">
        {submissionError ? (
          <div role="alert" className="mb-3 text-destructive text-sm">
            {submissionError}
          </div>
        ) : null}
        <div className="grid grid-cols-2 gap-3 sm:flex sm:justify-end">
          <Button
            type="button"
            variant="outline"
            className="h-11 w-full rounded-lg px-6 text-sm sm:w-auto"
            disabled={isSubmitting}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={isSubmitting || !canEdit}
            className="h-11 w-full rounded-lg bg-primary px-6 text-primary-foreground text-sm hover:bg-primary/90 sm:w-auto"
            onClick={handleSubmit}
          >
            {isSubmitting
              ? "Saving..."
              : submitLabel || (embedded ? "Save units" : "Submit")}
          </Button>
        </div>
      </div>
    </div>
  );

  if (embedded) {
    return editor;
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full max-w-full gap-0 overflow-x-hidden overflow-y-hidden border-l bg-background p-0 sm:max-w-xl"
      >
        <SheetHeader className="px-6 py-6 text-left">
          <SheetTitle className="text-2xl">Edit Unit of Measure</SheetTitle>
          <SheetDescription>
            Update product information and inventory levels
          </SheetDescription>
        </SheetHeader>
        {editor}
      </SheetContent>
    </Sheet>
  );
}
